import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { getImageUrl } from '../../utils/helpers';

/**
 * Shared admin gallery for /uploads/activities.
 * Used by Activity Types and Activity Management so images can be
 * imported/deleted without creating a session or type.
 */
const ImageGalleryManager = ({
  selectedUrl = '',
  onSelect,
  onDeleted,
  selectable = true,
  helperText = "Cliquez une vignette pour l'associer. Corbeille pour supprimer le fichier.",
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    image: null,
    usage: null,
    loading: false,
    error: '',
  });

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { data } = await axios.get('/api/activities/images');
      setImages(data || []);
    } catch (err) {
      setImages([]);
      setError(err.response?.data?.message || 'Impossible de charger la galerie');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      setError('');
      const body = new FormData();
      body.append('image', file);
      const { data } = await axios.post('/api/activity-types/upload-image', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchImages();
      if (selectable && onSelect && data) {
        onSelect(data);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'import de l'image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openDeleteDialog = async (image, event) => {
    event?.stopPropagation?.();
    setDeleteDialog({
      open: true,
      image,
      usage: null,
      loading: true,
      error: '',
    });
    try {
      const { data } = await axios.get(
        `/api/activity-types/images/${encodeURIComponent(image.filename)}/usage`
      );
      setDeleteDialog((prev) => ({ ...prev, usage: data, loading: false }));
    } catch (err) {
      setDeleteDialog((prev) => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || "Impossible de vérifier l'usage de l'image",
      }));
    }
  };

  const closeDeleteDialog = () => {
    if (deleteDialog.loading) return;
    setDeleteDialog({
      open: false,
      image: null,
      usage: null,
      loading: false,
      error: '',
    });
  };

  const confirmDelete = async () => {
    const image = deleteDialog.image;
    if (!image?.filename) return;
    try {
      setDeleteDialog((prev) => ({ ...prev, loading: true, error: '' }));
      await axios.delete(
        `/api/activity-types/images/${encodeURIComponent(image.filename)}?confirm=true`
      );
      await fetchImages();
      onDeleted?.(image);
      setDeleteDialog({
        open: false,
        image: null,
        usage: null,
        loading: false,
        error: '',
      });
    } catch (err) {
      setDeleteDialog((prev) => ({
        ...prev,
        loading: false,
        error: err.response?.data?.message || "Erreur lors de la suppression de l'image",
      }));
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Import…' : 'Importer une photo'}
        </Button>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchImages} disabled={loading}>
          Rafraîchir
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          hidden
          onChange={handleImport}
        />
        {helperText && (
          <Typography variant="body2" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {!loading && images.length === 0 ? (
        <Alert severity="info">Aucune image dans la galerie. Importez une photo JPG ou PNG.</Alert>
      ) : (
        <Grid container spacing={2}>
          {images.map((image, index) => {
            const isSelected = selectable && selectedUrl && selectedUrl === image.url;
            return (
              <Grid item xs={12} sm={6} md={4} key={image.filename || `image-${index}`}>
                <Box
                  sx={{
                    position: 'relative',
                    cursor: selectable && onSelect ? 'pointer' : 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 1,
                    border: isSelected ? '2px solid' : '1px solid #eee',
                    borderColor: isSelected ? 'primary.main' : '#eee',
                    borderRadius: 1,
                    bgcolor: isSelected ? 'action.selected' : 'transparent',
                    '&:hover': selectable && onSelect ? { opacity: 0.92 } : undefined,
                  }}
                  onClick={() => selectable && onSelect?.(image)}
                >
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Supprimer l'image"
                    onClick={(e) => openDeleteDialog(image, e)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': { bgcolor: 'error.light', color: '#fff' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <Box
                    sx={{
                      width: '100%',
                      paddingTop: '75%',
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 1,
                      bgcolor: '#f5f5f5',
                    }}
                  >
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.filename || ''}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" noWrap title={image.filename}>
                    {image.filename}
                    {isSelected ? ' · sélectionnée' : ''}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Supprimer l&apos;image</Typography>
            <IconButton onClick={closeDeleteDialog} size="small" disabled={deleteDialog.loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {deleteDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteDialog.error}</Alert>
          )}
          {deleteDialog.loading && !deleteDialog.usage ? (
            <Typography>Vérification de l&apos;usage…</Typography>
          ) : (
            <DialogContentText component="div">
              <Typography paragraph>
                Supprimer définitivement « <strong>{deleteDialog.image?.filename}</strong> » du serveur ?
              </Typography>
              {(deleteDialog.usage?.typeCount > 0 || deleteDialog.usage?.activityCount > 0) && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Utilisée par {deleteDialog.usage.typeCount} type(s) et{' '}
                  {deleteDialog.usage.activityCount} séance(s). Les références seront vidées.
                </Alert>
              )}
              {deleteDialog.usage?.types?.length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Types : {deleteDialog.usage.types.map((row) => row.displayName || row.name).join(', ')}
                </Typography>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteDialog.loading}>
            {t('cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Suppression…' : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ImageGalleryManager;
